import React from 'react';
import type { PhuongThucThanhToan, PhuongThucThanhToanInput } from '@/types/schemas';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import type { FormValues } from './schema';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function PhuongThucThanhToan() {
  const {
    data: paymentMethods,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addPaymentMethod,
    updateItem: updatePaymentMethod,
    deleteItem: deletePaymentMethod,
    refreshData: refreshPaymentMethods,
    isLoading,
    searchData
  } = useCRUD<PhuongThucThanhToan, PhuongThucThanhToanInput>({
    endpoint: QUERY_KEYS.PHUONG_THUC_THANH_TOAN,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<PhuongThucThanhToan>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : paymentMethods || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddPaymentMethod = async (data: PhuongThucThanhToanInput) => {
    await addPaymentMethod(data);
    await refreshPaymentMethods();
  };

  const handleUpdatePaymentMethod = async (data: PhuongThucThanhToanInput) => {
    if (selectedObj) {
      await updatePaymentMethod(selectedObj.uuid, data);
      await refreshPaymentMethods();
    }
  };

  const handleFormSubmit = async (data: FormValues) => {
    try {
      if (formMode === 'add') {
        await handleAddPaymentMethod({
          ma_pttt: data.ma_pttt,
          ten_pttt: data.ten_pttt,
          ten_pttt2: data.ten_pttt2,
          ma_ph_thuc_hddt: data.ma_ph_thuc_hddt,
          status: data.status
        });
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdatePaymentMethod({
          ma_pttt: data.ma_pttt,
          ten_pttt: data.ten_pttt,
          ten_pttt2: data.ten_pttt2,
          ma_ph_thuc_hddt: data.ma_ph_thuc_hddt,
          status: data.status
        });
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedObj) {
      try {
        await deletePaymentMethod(selectedObj.uuid);
        await refreshPaymentMethods();
        handleCloseDelete();
        clearSelection();
      } catch (error) {
        console.error('Error deleting payment method:', error);
      }
    }
  };

  return (
    <div className='flex h-screen flex-col lg:overflow-hidden'>
      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title='Xoá dữ liệu'
          content='Bạn có chắc chắn muốn xoá không?'
        />
      )}
      {showForm && (
        <FormDialog
          open={showForm}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onAddButtonClick={() => {
            // First close the current dialog
            handleCloseForm();
            // Then clear the selection to ensure no data is carried over
            clearSelection();
            // Finally open a new add dialog
            setTimeout(() => {
              handleAddClick();
            }, 100); // Small delay to ensure state updates properly
          }}
          onEditButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDeleteButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopyButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={() => selectedObj && handleEditClick()}
          onDeleteClick={() => selectedObj && handleDeleteClick()}
          onCopyClick={() => selectedObj && handleCopyClick()}
          onViewClick={() => selectedObj && handleViewClick()}
          onRefreshClick={async () => {
            await refreshPaymentMethods();
            if (selectedObj?.uuid) {
              await fetchDetail(selectedObj.uuid);
            }
          }}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>
    </div>
  );
}
